import {
  type ChangeEvent,
  Children,
  type ComponentProps,
  type HTMLProps,
  type ReactElement,
  useState,
} from "react";
import { DayPicker, type DropdownProps } from "react-day-picker";
import { ChevronIcon } from "vega-icons";

import { buttonVariants } from "@vega-core/components/shadcn/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@vega-core/components/shadcn/select";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

export type CalendarProps = ComponentProps<typeof DayPicker>;

const cellAndSelectedClasses = `
  [&:has([aria-selected].day-range-end)]:rounded-r-containerCorner 
  [&:has([aria-selected].day-outside)]:bg-background/component/80
  [&:has([aria-selected])]:bg-background/component 
  [&:has([aria-selected])]:rounded-none 
  [&:has([aria-selected])]:text-background/primary 
  first:[&:has([aria-selected])]:rounded-l-containerCorner 
  last:[&:has([aria-selected])]:rounded-r-containerCorner`;

export const CoreCalenderClasses = (
  props: CalendarProps,
  classNames: CalendarProps["classNames"],
) => ({
  caption_dropdowns: "flex justify-center gap-1 w-full",
  caption: "flex justify-center pt-1 relative items-center",
  caption_label: cnTwMerge("body-semi-large-semibold", {
    hidden: props.captionLayout === "dropdown",
  }),
  cell: `h-9 w-9 text-center body-base-medium p-0 relative  focus-within:relative focus-within:z-20 defaultTransition ${
    cellAndSelectedClasses
  }`,
  day: cnTwMerge(
    buttonVariants({ variant: "ghost" }),
    "h-9 w-9 p-0 body-base-medium aria-selected:opacity-100",
  ),
  day_disabled: "text-text/disabled cursor-none",
  day_hidden: "invisible",
  day_outside: "day-outside ",
  day_range_end: "day-range-end",
  day_range_middle:
    "aria-selected:bg-transparent aria-selected:text-text/primary rounded-none bg-none ",
  day_selected:
    "bg-text/primary text-background/primary hover:bg-text/primary/50 ",
  day_today: "bg-background/secondary",
  head_cell:
    "text-text/secondary rounded-containerCorner w-9 body-base-medium ",
  head_row: "flex",
  month: "space-y-4",
  months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
  nav: "space-x-1 flex items-center",
  nav_button: cnTwMerge(
    buttonVariants({ variant: "outline" }),
    "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
  ),
  nav_button_next: "absolute right-1",
  nav_button_previous: "absolute left-1",
  row: "flex w-full mt-2",
  table: "w-full border-collapse space-y-1",
  ...classNames,
});

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  name,
  ...props
}: CalendarProps & { name: string }) {
  return (
    <DayPicker
      className={cnTwMerge("p-3", className)}
      classNames={CoreCalenderClasses(props, classNames)}
      components={{
        Dropdown: ({ value, onChange, children }: DropdownProps) => {
          const [isOpen, setIsOpen] = useState(false);
          const options = Children.toArray(children) as ReactElement<
            HTMLProps<HTMLOptionElement>
          >[];
          const selected = options.find((child) => child.props.value === value);
          const handleChange = (value: string) => {
            const changeEvent = {
              target: { value },
            } as ChangeEvent<HTMLSelectElement>;
            onChange?.(changeEvent);
          };

          return (
            <Select
              onOpenChange={(setOpen) => setIsOpen(setOpen)}
              onValueChange={(value) => {
                handleChange(value);
              }}
              value={value?.toString()}
            >
              <SelectTrigger
                className="w-full focus:ring-0"
                data-testid={`${name}-calendar-trigger`}
                isOpen={isOpen}
              >
                <SelectValue>{selected?.props.children}</SelectValue>
              </SelectTrigger>
              <SelectContent className="w-full z-[65]" position="popper">
                {options.map((option) => (
                  <SelectItem
                    data-testid={`${name}-calendar-option-${option.props.value}`}
                    key={`${option.props.value}`}
                    value={option.props.value?.toString() ?? ""}
                  >
                    {option.props.children}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        },
        IconLeft: () => (
          <ChevronIcon
            className="h-4 w-4 rotate-90"
            data-testid={`${name}-calender-left`}
          />
        ),
        IconRight: () => (
          <ChevronIcon
            className="h-4 w-4 -rotate-90"
            data-testid={`${name}-calender-right`}
          />
        ),
      }}
      data-testid={`${name}-calendar`}
      showOutsideDays={showOutsideDays}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
